import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../providers/AuthProvider';
import ProtectedRoute from '../components/routers/ProtectedRoute';

/**
 * Props for the DashboardRoute component
 */
interface DashboardRouteProps {
  children: React.ReactElement;
  /**
   * Optional minimum loading time in milliseconds
   * @default 300
   */
  minimumLoaderTime?: number;
  /**
   * Whether to check if user is a seller
   * @default false
   */
  requireSeller?: boolean;
}

/**
 * DashboardRoute extends ProtectedRoute with additional seller-specific checks.
 * It ensures that only authenticated users (and optionally sellers) can access the wrapped route.
 * 
 * Seller status:
 * - is_seller = 0: Pending seller (restricted access, only profile and dashboard)
 * - is_seller = 1: Approved seller (full access)
 * - is_seller = 3: Not a seller (no access)
 */
const DashboardRoute: React.FC<DashboardRouteProps> = ({
  children,
  minimumLoaderTime = 300,
  requireSeller = false
}) => {
  const { seller, isAuthenticated } = useAuth();
  const { pathname } = window.location;
  
  // Check if the current route is profile or dashboard
  const isProfileRoute = pathname.includes('/profile');
  const isDashboardRoute = pathname === '/' || pathname === '/dashboard';
  const isAllowedForPendingSeller = isProfileRoute || isDashboardRoute;

  return (
    <ProtectedRoute minimumLoaderTime={minimumLoaderTime}>
      {isAuthenticated && (
        <>
          {/* Redirect non-sellers (is_seller = 3) to restricted page */}
          {requireSeller && seller?.is_seller === "3" && (
            <Navigate to="/restricted" />
          )}
          
          {/* Pending sellers (is_seller = 0) can only access profile and dashboard */}
          {seller?.is_seller === "0" && !isAllowedForPendingSeller ? (
            <Navigate to="/profile" />
          ) : (
            // Render children when all checks pass
            children
          )}
        </>
      )}
    </ProtectedRoute>
  );
};

export default DashboardRoute;
